import React, { useState, useEffect } from "react";
import {
  ChevronLeft,
  ChevronRight,
  X,
  Filter,
  Home,
  Building,
  CarFront,
  Sofa,
  Wind,
  Users,
  Award,
  CheckCircle,
  Star,
} from "lucide-react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";

const GalleryPage = () => {
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const categories = [
    { id: "all", label: "All Projects", icon: <Home size={18} />, count: 28 },
    {
      id: "residential",
      label: "Residential",
      icon: <Home size={18} />,
      count: 12,
    },
    {
      id: "commercial",
      label: "Commercial",
      icon: <Building size={18} />,
      count: 8,
    },
    {
      id: "carpet",
      label: "Carpet Cleaning",
      icon: <CarFront size={18} />,
      count: 6,
    },
    {
      id: "upholstery",
      label: "Upholstery",
      icon: <Sofa size={18} />,
      count: 5,
    },
    { id: "specialty", label: "Specialty", icon: <Wind size={18} />, count: 7 },
  ];

  const galleryImages = [
    // Residential Cleaning
    {
      id: 1,
      category: "residential",
      title: "Modern Apartment Cleaning",
      description: "Complete deep cleaning of a 3-bedroom apartment",
      beforeAfter: true,
      image:
        "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      beforeImage:
        "https://images.unsplash.com/photo-1583947581924-860bda6a26df?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      location: "South Yarra, Melbourne",
      date: "2024-01-15",
    },
    {
      id: 2,
      category: "residential",
      title: "Family House Spring Clean",
      description: "Post-renovation cleaning for a family home",
      image:
        "https://images.unsplash.com/photo-1565538810643-b5bdb714032a?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      location: "Toorak, Melbourne",
      date: "2024-01-20",
    },
    {
      id: 3,
      category: "residential",
      title: "Move-Out Cleaning",
      description: "End of lease cleaning for rental property",
      beforeAfter: true,
      image:
        "https://images.unsplash.com/photo-1595526114035-0d45ed16cfbf?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      beforeImage:
        "https://images.unsplash.com/photo-1584622650111-993a426fbf0a?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      location: "CBD, Melbourne",
      date: "2024-01-25",
    },

    // Commercial Cleaning
    {
      id: 4,
      category: "commercial",
      title: "Office Building Cleaning",
      description: "Daily maintenance for corporate offices",
      image:
        "https://images.unsplash.com/photo-1497366754035-f200968a6e72?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      location: "Collins Street, Melbourne",
      date: "2024-01-18",
    },
    {
      id: 5,
      category: "commercial",
      title: "Retail Store Cleaning",
      description: "Nightly cleaning for shopping center store",
      image:
        "https://images.unsplash.com/photo-1497366216548-37526070297c?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      location: "Chadstone Shopping Centre",
      date: "2024-01-22",
    },
    {
      id: 6,
      category: "commercial",
      title: "Medical Facility Disinfection",
      description: "COVID-safe cleaning protocol implementation",
      image:
        "https://images.unsplash.com/photo-1579684385127-1ef15d508118?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      location: "Royal Melbourne Hospital",
      date: "2024-01-30",
    },

    // Carpet Cleaning
    {
      id: 7,
      category: "carpet",
      title: "Stain Removal Service",
      description: "Deep stain extraction from wool carpet",
      beforeAfter: true,
      image:
        "https://images.unsplash.com/photo-1542831371-29b0f74f9713?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      beforeImage:
        "https://images.unsplash.com/photo-1584622650111-993a426fbf0a?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      location: "Brighton, Melbourne",
      date: "2024-01-12",
    },
    {
      id: 8,
      category: "carpet",
      title: "Commercial Carpet Maintenance",
      description: "Regular carpet cleaning for hotel lobby",
      image:
        "https://images.unsplash.com/photo-1542648747-2f0592c1cc8a?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      location: "Crown Towers, Melbourne",
      date: "2024-01-28",
    },

    // Upholstery Cleaning
    {
      id: 9,
      category: "upholstery",
      title: "Leather Sofa Restoration",
      description: "Professional leather cleaning and conditioning",
      beforeAfter: true,
      image:
        "https://images.unsplash.com/photo-1555041469-a586c61ea9bc?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      beforeImage:
        "https://images.unsplash.com/photo-1586023492125-27b2c045efd7?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      location: "Hawthorn, Melbourne",
      date: "2024-01-14",
    },
    {
      id: 10,
      category: "upholstery",
      title: "Office Chair Cleaning",
      description: "Fabric cleaning for corporate office chairs",
      image:
        "https://images.unsplash.com/photo-1560185007-5f0bb1866cab?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      location: "Docklands, Melbourne",
      date: "2024-01-19",
    },

    // Specialty Cleaning
    {
      id: 11,
      category: "specialty",
      title: "Post-Construction Cleanup",
      description: "Construction dust removal from new build",
      beforeAfter: true,
      image:
        "https://images.unsplash.com/photo-1503387769-00a112127ca0?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      beforeImage:
        "https://images.unsplash.com/photo-1503387769-00a112127ca0?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80&blur=20",
      location: "Port Melbourne",
      date: "2024-01-24",
    },
    {
      id: 12,
      category: "specialty",
      title: "Window Cleaning - High Rise",
      description: "Professional window cleaning for apartment building",
      image:
        "https://images.unsplash.com/photo-1512917774080-9991f1c4c750?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      location: "St Kilda Road, Melbourne",
      date: "2024-01-26",
    },
  ];

  const filteredImages =
    selectedCategory === "all"
      ? galleryImages
      : galleryImages.filter((image) => image.category === selectedCategory);

  const openLightbox = (index) => {
    setCurrentImageIndex(index);
    setLightboxOpen(true);
    document.body.style.overflow = "hidden";
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    document.body.style.overflow = "auto";
  };

  const nextImage = () => {
    setCurrentImageIndex((prevIndex) =>
      prevIndex === filteredImages.length - 1 ? 0 : prevIndex + 1,
    );
  };

  const prevImage = () => {
    setCurrentImageIndex((prevIndex) =>
      prevIndex === 0 ? filteredImages.length - 1 : prevIndex - 1,
    );
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!lightboxOpen) return;
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowRight") nextImage();
      if (e.key === "ArrowLeft") prevImage();
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [lightboxOpen]);

  const stats = [
    {
      value: "28+",
      label: "Projects Completed",
      icon: <CheckCircle className="text-blue-500" size={20} />,
    },
    {
      value: "98%",
      label: "Client Satisfaction",
      icon: <Star className="text-amber-500" size={20} />,
    },
    {
      value: "15+",
      label: "Years Experience",
      icon: <Award className="text-green-500" size={20} />,
    },
    {
      value: "500+",
      label: "Happy Customers",
      icon: <Users className="text-purple-500" size={20} />,
    },
  ];

  const beforeAfterData = [
    { before: "Stained Carpet", after: "Spotless Carpet", success: "95%" },
    { before: "Dirty Upholstery", after: "Fresh & Clean", success: "100%" },
    { before: "Grimy Windows", after: "Crystal Clear", success: "98%" },
    {
      before: "Messy Post-Construction",
      after: "Move-in Ready",
      success: "100%",
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      <Helmet>
        <title>Our Cleaning Work in Melbourne VIC</title>

        <meta
          name="description"
          content="View our cleaning work gallery showcasing real residential and commercial cleaning projects completed across Melbourne VIC."
        />

        <meta
          name="keywords"
          content="
        Carpet cleaning Melbourne,
        Upholstery cleaning Melbourne,
        End of lease cleaning Melbourne,
        Mattress cleaning Melbourne,
        Professional cleaning Melbourne,

        Carpet cleaning Dandenong,
        Upholstery cleaning Dandenong,
        End of lease cleaning Dandenong,

        Carpet cleaning Hallam,
        Upholstery cleaning Hallam,

        Carpet cleaning Hampton Park,
        Carpet cleaning Clayton,
        Carpet cleaning Keysborough,
        Carpet cleaning Glen Waverley,
        Carpet cleaning Mount Waverley,
        Carpet cleaning Noble Park,

        Carpet cleaning Lynbrook,
        Carpet cleaning Doveton,

        Mattress cleaning Mornington,
        Carpet cleaning Mornington,
        Carpet cleaning Rye,
        Carpet cleaning Rosebud,

        Professional cleaners Melbourne,
        Steam carpet cleaning Melbourne,
        Deep cleaning services Melbourne,
        Residential cleaning Melbourne,
        Commercial cleaning Melbourne
        "
        />

        <meta name="robots" content="index, follow" />
        <meta
          name="author"
          content="Professional Cleaning Services Melbourne"
        />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ImageGallery",
            name: "Knight Cleanings Work Gallery",
            url: "https://knightcleanings.com.au/gallery",
            description:
              "View our portfolio of professional cleaning work across Melbourne VIC", // Optional but recommended
            image: [
              // Optional but recommended - list some featured gallery images
              "https://knightcleanings.com.au/gallery/image1.jpg",
              "https://knightcleanings.com.au/gallery/image2.jpg",
              "https://knightcleanings.com.au/gallery/image3.jpg",
            ],
            author: {
              "@type": "CleaningService",
              name: "Knight Cleanings",
              url: "https://knightcleanings.com.au",
            },
          })}
        </script>
      </Helmet>
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-r from-blue-700 via-indigo-700 to-blue-800 text-white py-20">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-10"
          style={{
            backgroundImage:
              'url("https://images.unsplash.com/photo-1583947581924-860bda6a26df?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80")',
          }}
        ></div>
        <div className="max-w-7xl mx-auto px-4 relative">
          <div className="text-center">
            <h1 className="text-5xl font-bold mb-6">Our Work Gallery</h1>
            <h2 className="text-3xl font-semibold mb-4">
              Showcasing Our Cleaning Excellence in Melbourne
            </h2>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto mb-8">
              Explore our portfolio of cleaning projects. See the transformation
              we bring to homes and businesses across Melbourne.
            </p>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto mt-12">
              {stats.map((stat, index) => (
                <div
                  key={index}
                  className="bg-white/10 backdrop-blur-sm p-4 rounded-xl"
                >
                  <div className="flex items-center justify-center gap-2 mb-2">
                    {stat.icon}
                    <div className="text-3xl font-bold">{stat.value}</div>
                  </div>
                  <div className="text-sm text-blue-200 text-center">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Main Gallery */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          {/* Category Filters */}
          <div className="mb-12">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-3xl font-bold text-gray-900">
                  Project Portfolio
                </h2>
                <p className="text-gray-600 mt-2">
                  Browse our recent cleaning projects by category
                </p>
              </div>
              <div className="hidden md:flex items-center gap-2 text-blue-600">
                <Filter size={18} />
                <span className="font-medium">Filter</span>
              </div>
            </div>

            <div className="flex flex-wrap gap-3">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`flex items-center gap-2 px-5 py-3 rounded-full transition-all duration-300 ${
                    selectedCategory === category.id
                      ? "bg-gradient-to-r from-blue-600 to-indigo-700 text-white shadow-lg"
                      : "bg-white text-gray-700 border border-gray-200 hover:border-blue-300 hover:shadow-md"
                  }`}
                >
                  {category.icon}
                  <span className="font-semibold">{category.label}</span>
                  <span
                    className={`text-xs px-2 py-1 rounded-full ${
                      selectedCategory === category.id
                        ? "bg-white/20"
                        : "bg-blue-100 text-blue-700"
                    }`}
                  >
                    {category.count}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Gallery Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredImages.map((image, index) => (
              <div
                key={image.id}
                className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 group cursor-pointer"
                onClick={() => openLightbox(index)}
              >
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={image.image}
                    alt={image.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  {image.beforeAfter && (
                    <div className="absolute top-4 left-4">
                      <span className="bg-gradient-to-r from-amber-400 to-yellow-400 text-gray-900 text-xs font-bold px-3 py-1 rounded-full">
                        BEFORE/AFTER
                      </span>
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </div>

                <div className="p-6">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-medium text-blue-600 bg-blue-50 px-3 py-1 rounded-full">
                      {image.category.charAt(0).toUpperCase() +
                        image.category.slice(1)}
                    </span>
                    <span className="text-sm text-gray-500">{image.date}</span>
                  </div>

                  <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
                    {image.title}
                  </h3>
                  <p className="text-gray-600 mb-4">{image.description}</p>

                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <span>📍 {image.location}</span>
                  </div>

                  {image.beforeAfter && (
                    <div className="mt-4 pt-4 border-t border-gray-100">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-500">
                          Before/After Available
                        </span>
                        <span className="text-blue-600 font-medium">
                          View Details →
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Empty State */}
          {filteredImages.length === 0 && (
            <div className="text-center py-20">
              <div className="text-gray-400 mb-4">📷</div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                No Projects Found
              </h3>
              <p className="text-gray-600">
                We're currently adding more projects to this category.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Before/After Section */}
      <section className="bg-gradient-to-r from-blue-50 to-indigo-50 py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Transformation Results
            </h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              See the dramatic results we achieve with our professional cleaning
              methods
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {beforeAfterData.map((item, index) => (
              <div
                key={index}
                className="bg-white p-6 rounded-xl shadow-sm border border-gray-100"
              >
                <div className="text-center mb-4">
                  <div className="inline-flex items-center gap-2 bg-green-100 text-green-700 px-4 py-2 rounded-full text-sm font-bold">
                    <CheckCircle size={16} />
                    {item.success} Success Rate
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                    <span className="font-medium text-red-700">Before</span>
                    <span className="text-sm text-gray-600">{item.before}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                    <span className="font-medium text-green-700">After</span>
                    <span className="text-sm text-gray-600">{item.after}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-700 rounded-2xl p-12 text-white overflow-hidden relative">
            <div
              className="absolute inset-0 bg-cover bg-center opacity-10"
              style={{
                backgroundImage:
                  'url("https://images.unsplash.com/photo-1583947581924-860bda6a26df?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80")',
              }}
            ></div>

            <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              <div>
                <h3 className="text-3xl font-bold mb-4">
                  Ready for Your Transformation?
                </h3>
                <p className="text-blue-100 text-lg mb-6">
                  Book a professional cleaning service and see the difference
                  for yourself. Get a free quote today!
                </p>
                <div className="flex items-center gap-4 flex-wrap">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="text-green-300" size={20} />
                    <span className="text-blue-100">
                      Before/After Photos Provided
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Award className="text-amber-300" size={20} />
                    <span className="text-blue-100">
                      Satisfaction Guaranteed
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/contact"
                  className="bg-white text-blue-700 hover:bg-gray-100 font-bold px-8 py-4 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 text-center"
                >
                  Get Free Quote
                </Link>
                <Link
                  to="/services"
                  className="border-2 border-white text-white hover:bg-white/10 font-semibold px-8 py-4 rounded-full transition-all duration-300 text-center"
                >
                  View Services
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      {lightboxOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4">
          <button
            onClick={closeLightbox}
            className="absolute top-6 right-6 text-white hover:text-gray-300 transition-colors"
          >
            <X size={28} />
          </button>

          <button
            onClick={prevImage}
            className="absolute left-6 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300 transition-colors"
          >
            <ChevronLeft size={32} />
          </button>

          <button
            onClick={nextImage}
            className="absolute right-6 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300 transition-colors"
          >
            <ChevronRight size={32} />
          </button>

          <div className="max-w-6xl w-full">
            <div className="bg-white rounded-2xl overflow-hidden">
              <div className="relative h-[60vh]">
                <img
                  src={filteredImages[currentImageIndex].image}
                  alt={filteredImages[currentImageIndex].title}
                  className="w-full h-full object-contain"
                />
              </div>

              <div className="p-8">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-2xl font-bold text-gray-900">
                    {filteredImages[currentImageIndex].title}
                  </h3>
                  <span className="text-sm text-gray-500">
                    {currentImageIndex + 1} / {filteredImages.length}
                  </span>
                </div>

                <p className="text-gray-600 mb-4">
                  {filteredImages[currentImageIndex].description}
                </p>

                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Location:</span>
                    <span className="font-medium text-gray-900 ml-2">
                      {filteredImages[currentImageIndex].location}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500">Date:</span>
                    <span className="font-medium text-gray-900 ml-2">
                      {filteredImages[currentImageIndex].date}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500">Category:</span>
                    <span className="font-medium text-gray-900 ml-2 capitalize">
                      {filteredImages[currentImageIndex].category}
                    </span>
                  </div>
                  {filteredImages[currentImageIndex].beforeAfter && (
                    <div>
                      <span className="text-gray-500">Type:</span>
                      <span className="font-medium text-green-600 ml-2">
                        Before/After Available
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GalleryPage;
